import React, { useRef } from 'react';
import { Button } from './ui/Button';
import { Book } from '../data/bookContent';
import { BookOpenIcon, ShoppingCartIcon, CheckCircleIcon, HeartIcon, SunIcon, MoonIcon, SparklesIcon, ArrowDownIcon, XCircleIcon, ZapIcon, ClockIcon, SmileIcon } from 'lucide-react';
import { Footer } from './Footer';
interface BookCoverProps {
  book: Book;
  onStart: () => void;
  onReadIntro: () => void;
}
export function BookCover({
  book,
  onStart,
  onReadIntro
}: BookCoverProps) {
  const pricingRef = useRef<HTMLDivElement>(null);
  const scrollToPricing = () => {
    pricingRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="bg-[#1a2e40] text-amber-50" data-id="element-5">
      {/* ===== HERO SECTION ===== */}
      <section className="relative min-h-screen flex flex-col items-center justify-center p-6 text-center overflow-hidden" data-id="element-6">
        {/* Subtle radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(217,169,78,0.08)_0%,_transparent_70%)]" data-id="element-7" />

        <div className="relative max-w-2xl w-full flex flex-col items-center space-y-8" data-id="element-8">
          {/* Cover Image */}
          <div className="w-56 md:w-64 shadow-2xl shadow-black/50 rounded-lg overflow-hidden border-4 border-amber-700/30" data-id="element-9">
            <img src={book.coverImage} alt={`Portada del libro ${book.title}`} className="w-full h-full object-cover" data-id="element-10" />
          </div>

          {/* Main Headline */}
          <div className="space-y-5" data-id="element-11">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-serif font-bold text-amber-100 leading-tight" data-id="element-12">
              Días vacíos nunca más
            </h1>
            <p className="text-xl md:text-2xl text-amber-200/80 font-light max-w-xl mx-auto leading-relaxed" data-id="element-13">
              Descubre cómo micro-hábitos flexibles pueden devolverte el
              propósito, la vitalidad y la alegría diaria — en solo 15 minutos.
            </p>
          </div>

          {/* CTAs */}
          <div className="pt-2 w-full max-w-sm space-y-4" data-id="element-14">
            <Button onClick={scrollToPricing} size="xl" className="w-full bg-amber-600 hover:bg-amber-500 text-white border-none shadow-xl transform transition hover:scale-105" data-id="element-15">
              <ShoppingCartIcon className="mr-3 h-7 w-7" data-id="element-16" />
              Quiero Mi Copia
            </Button>
          </div>

          {/* Scroll hint */}
          <button onClick={scrollToPricing} className="mt-8 text-amber-400/40 hover:text-amber-400/70 transition-colors" aria-label="Desplazar hacia abajo" data-id="element-17">
            <ArrowDownIcon className="w-6 h-6 animate-bounce" data-id="element-18" />
          </button>
        </div>
      </section>

      {/* ===== PAIN / PROBLEM SECTION ===== */}
      <section className="py-20 px-6 bg-[#162636]" data-id="element-19">
        <div className="max-w-3xl mx-auto text-center space-y-10" data-id="element-20">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-amber-100" data-id="element-21">
            ¿Te identificas con esto?
          </h2>

          <div className="grid gap-6 md:grid-cols-2 text-left" data-id="element-22">
            <div className="bg-white/5 border border-red-400/20 rounded-2xl p-6 space-y-3" data-id="element-23">
              <ClockIcon className="w-8 h-8 text-red-400/70" data-id="element-24" />
              <h3 className="text-xl font-bold text-amber-100" data-id="element-25">
                Días sin estructura
              </h3>
              <p className="text-amber-200/60 text-lg leading-relaxed" data-id="element-26">
                Desde el retiro, los días se sienten largos y vacíos. Te
                levantas sin un plan claro y la jornada se escapa entre las
                manos.
              </p>
            </div>

            <div className="bg-white/5 border border-red-400/20 rounded-2xl p-6 space-y-3" data-id="element-27">
              <ZapIcon className="w-8 h-8 text-red-400/70" data-id="element-28" />
              <h3 className="text-xl font-bold text-amber-100" data-id="element-29">
                Pérdida de energía
              </h3>
              <p className="text-amber-200/60 text-lg leading-relaxed" data-id="element-30">
                La vitalidad que antes tenías parece haberse ido. Te sientes
                cansada antes de que el día siquiera comience.
              </p>
            </div>

            <div className="bg-white/5 border border-red-400/20 rounded-2xl p-6 space-y-3" data-id="element-31">
              <XCircleIcon className="w-8 h-8 text-red-400/70" data-id="element-32" />
              <h3 className="text-xl font-bold text-amber-100" data-id="element-33">
                Rutinas que no funcionan
              </h3>
              <p className="text-amber-200/60 text-lg leading-relaxed" data-id="element-34">
                Has probado horarios estrictos, listas interminables y programas
                de ejercicio que te dejaron agotada y frustrada.
              </p>
            </div>

            <div className="bg-white/5 border border-red-400/20 rounded-2xl p-6 space-y-3" data-id="element-35">
              <HeartIcon className="w-8 h-8 text-red-400/70" data-id="element-36" />
              <h3 className="text-xl font-bold text-amber-100" data-id="element-37">
                Rigidez emocional
              </h3>
              <p className="text-amber-200/60 text-lg leading-relaxed" data-id="element-38">
                A veces sientes que has perdido la conexión contigo misma.
                Quieres sentirte viva, pero no sabes por dónde empezar.
              </p>
            </div>
          </div>

          <p className="text-2xl md:text-3xl font-serif text-amber-200/90 italic max-w-xl mx-auto pt-4" data-id="element-39">
            "No es tu culpa. Las soluciones que probaste no fueron diseñadas
            para ti."
          </p>
        </div>
      </section>

      {/* ===== WHY OLD SOLUTIONS FAIL ===== */}
      <section className="py-20 px-6 bg-[#1a2e40]" data-id="element-40">
        <div className="max-w-3xl mx-auto text-center space-y-10" data-id="element-41">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-amber-100" data-id="element-42">
            Cuando las grandes rutinas fracasan,
            <br data-id="element-43" />
            <span className="text-amber-400" data-id="element-44">los micro-hábitos triunfan</span>
          </h2>

          <div className="space-y-6 text-left max-w-2xl mx-auto" data-id="element-45">
            <div className="flex items-start gap-4" data-id="element-46">
              <XCircleIcon className="w-6 h-6 text-red-400/70 flex-shrink-0 mt-1" data-id="element-47" />
              <p className="text-lg text-amber-200/70 leading-relaxed" data-id="element-48">
                <span className="font-bold text-amber-100" data-id="element-49">
                  Las rutinas rígidas
                </span>{' '}
                ignoran que tu energía cambia cada día. Te obligan a rendir
                igual un lunes que un viernes, sin importar cómo te sientas.
              </p>
            </div>
            <div className="flex items-start gap-4" data-id="element-50">
              <XCircleIcon className="w-6 h-6 text-red-400/70 flex-shrink-0 mt-1" data-id="element-51" />
              <p className="text-lg text-amber-200/70 leading-relaxed" data-id="element-52">
                <span className="font-bold text-amber-100" data-id="element-53">
                  Los programas genéricos
                </span>{' '}
                no consideran los cambios emocionales y físicos de esta etapa de
                la vida. No fueron pensados para ti.
              </p>
            </div>
            <div className="flex items-start gap-4" data-id="element-54">
              <XCircleIcon className="w-6 h-6 text-red-400/70 flex-shrink-0 mt-1" data-id="element-55" />
              <p className="text-lg text-amber-200/70 leading-relaxed" data-id="element-56">
                <span className="font-bold text-amber-100" data-id="element-57">
                  La fuerza de voluntad sola
                </span>{' '}
                no alcanza. Necesitas un sistema que trabaje contigo, no contra
                ti.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== UNIQUE MECHANISM ===== */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#162636] to-[#1a2e40]" data-id="element-58">
        <div className="max-w-3xl mx-auto text-center space-y-10" data-id="element-59">
          <SparklesIcon className="w-12 h-12 text-amber-400 mx-auto" data-id="element-60" />
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-amber-100" data-id="element-61">
            El Método de los Micro-hábitos Flexibles
          </h2>
          <p className="text-xl text-amber-200/70 max-w-2xl mx-auto leading-relaxed" data-id="element-62">
            Un sistema que se adapta a cómo te sientes cada día, con acciones
            pequeñas ajustadas a tres niveles de energía.
          </p>

          <div className="grid gap-6 md:grid-cols-3 pt-4" data-id="element-63">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-8 space-y-4" data-id="element-64">
              <SunIcon className="w-10 h-10 text-amber-400 mx-auto" data-id="element-65" />
              <h3 className="text-xl font-bold text-amber-100" data-id="element-66">Energía Alta</h3>
              <p className="text-amber-200/60 text-lg" data-id="element-67">
                Días buenos: estiramientos activos, caminata suave, preparar un
                snack nutritivo.
              </p>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-8 space-y-4" data-id="element-68">
              <SmileIcon className="w-10 h-10 text-amber-300 mx-auto" data-id="element-69" />
              <h3 className="text-xl font-bold text-amber-100" data-id="element-70">
                Energía Media
              </h3>
              <p className="text-amber-200/60 text-lg" data-id="element-71">
                Días normales: respiración consciente, hidratación intencional,
                movimiento en silla.
              </p>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-8 space-y-4" data-id="element-72">
              <MoonIcon className="w-10 h-10 text-amber-200/70 mx-auto" data-id="element-73" />
              <h3 className="text-xl font-bold text-amber-100" data-id="element-74">Energía Baja</h3>
              <p className="text-amber-200/60 text-lg" data-id="element-75">
                Días difíciles: solo 1 minuto de calma, un vaso de agua, una
                intención amable.
              </p>
            </div>
          </div>

          <p className="text-xl text-amber-300/90 font-serif italic pt-4" data-id="element-76">
            "Pequeñas victorias cada día, sin importar cómo te sientas."
          </p>
        </div>
      </section>

      {/* ===== WHAT'S INSIDE ===== */}
      <section className="py-20 px-6 bg-[#1a2e40]" data-id="element-77">
        <div className="max-w-3xl mx-auto text-center space-y-10" data-id="element-78">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-amber-100" data-id="element-79">
            Lo que encontrarás dentro
          </h2>
          <p className="text-lg text-amber-200/60" data-id="element-80">
            10 capítulos diseñados con cariño para acompañarte paso a paso.
          </p>

          <div className="space-y-4 text-left max-w-2xl mx-auto" data-id="element-81">
            <div className="bg-white/5 rounded-xl p-5 flex items-start gap-4" data-id="element-82">
              <span className="text-amber-500 font-serif font-bold text-2xl mt-0.5" data-id="element-83">
                1
              </span>
              <div data-id="element-84">
                <h3 className="font-bold text-amber-100 text-lg" data-id="element-85">
                  La Fórmula 1%
                </h3>
                <p className="text-amber-200/50" data-id="element-86">
                  Por qué pequeñas acciones crean grandes cambios.
                </p>
              </div>
            </div>
            <div className="bg-white/5 rounded-xl p-5 flex items-start gap-4" data-id="element-87">
              <span className="text-amber-500 font-serif font-bold text-2xl mt-0.5" data-id="element-88">
                2
              </span>
              <div data-id="element-89">
                <h3 className="font-bold text-amber-100 text-lg" data-id="element-90">
                  Tu Ritmo Personal
                </h3>
                <p className="text-amber-200/50" data-id="element-91">
                  Descubre cuándo tu cuerpo tiene más energía.
                </p>
              </div>
            </div>
            <div className="bg-white/5 rounded-xl p-5 flex items-start gap-4" data-id="element-92">
              <span className="text-amber-500 font-serif font-bold text-2xl mt-0.5" data-id="element-93">
                3
              </span>
              <div data-id="element-94">
                <h3 className="font-bold text-amber-100 text-lg" data-id="element-95">
                  Mañanas con Intención
                </h3>
                <p className="text-amber-200/50" data-id="element-96">
                  Un micro-hábito matutino que cambia todo tu día.
                </p>
              </div>
            </div>
            <div className="bg-white/5 rounded-xl p-5 flex items-start gap-4" data-id="element-97">
              <span className="text-amber-500 font-serif font-bold text-2xl mt-0.5" data-id="element-98">
                4–6
              </span>
              <div data-id="element-99">
                <h3 className="font-bold text-amber-100 text-lg" data-id="element-100">
                  Movimiento, Nutrición y Descanso
                </h3>
                <p className="text-amber-200/50" data-id="element-101">
                  Micro-hábitos para cada momento del día.
                </p>
              </div>
            </div>
            <div className="bg-white/5 rounded-xl p-5 flex items-start gap-4" data-id="element-102">
              <span className="text-amber-500 font-serif font-bold text-2xl mt-0.5" data-id="element-103">
                7–8
              </span>
              <div data-id="element-104">
                <h3 className="font-bold text-amber-100 text-lg" data-id="element-105">
                  Tu Rutina Personalizada + Superar Obstáculos
                </h3>
                <p className="text-amber-200/50" data-id="element-106">
                  Combina todo y aprende a no rendirte.
                </p>
              </div>
            </div>
            <div className="bg-white/5 rounded-xl p-5 flex items-start gap-4" data-id="element-107">
              <span className="text-amber-500 font-serif font-bold text-2xl mt-0.5" data-id="element-108">
                9–10
              </span>
              <div data-id="element-109">
                <h3 className="font-bold text-amber-100 text-lg" data-id="element-110">
                  Testimonios y Recursos
                </h3>
                <p className="text-amber-200/50" data-id="element-111">
                  Historias reales y herramientas para seguir creciendo.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-20 px-6 bg-[#162636]" data-id="element-112">
        <div className="max-w-3xl mx-auto text-center space-y-10" data-id="element-113">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-amber-100" data-id="element-114">
            Historias de mujeres como tú
          </h2>

          <div className="space-y-6 max-w-2xl mx-auto" data-id="element-115">
            <blockquote className="bg-white/5 border-l-4 border-amber-500 rounded-r-2xl p-6 text-left" data-id="element-116">
              <p className="text-xl text-amber-100/90 font-serif italic leading-relaxed" data-id="element-117">
                "Al principio era escéptica. ¿Qué pueden hacer 5 minutos? Pero
                después de un mes, mi dolor de espalda ha disminuido
                notablemente."
              </p>
              <footer className="mt-4 text-amber-400 font-bold" data-id="element-118">
                — Carmen, 68 años
              </footer>
            </blockquote>

            <blockquote className="bg-white/5 border-l-4 border-amber-500 rounded-r-2xl p-6 text-left" data-id="element-119">
              <p className="text-xl text-amber-100/90 font-serif italic leading-relaxed" data-id="element-120">
                "Lo que más me ha ayudado es la rutina de noche. Dormir mejor me
                ha cambiado el humor durante el día."
              </p>
              <footer className="mt-4 text-amber-400 font-bold" data-id="element-121">
                — Elena, 72 años
              </footer>
            </blockquote>

            <blockquote className="bg-white/5 border-l-4 border-amber-500 rounded-r-2xl p-6 text-left" data-id="element-122">
              <p className="text-xl text-amber-100/90 font-serif italic leading-relaxed" data-id="element-123">
                "No siento que estoy a dieta o en un entrenamiento militar. Son
                pequeñas caricias que me hago a mí misma cada día."
              </p>
              <footer className="mt-4 text-amber-400 font-bold" data-id="element-124">
                — Rosa, 66 años
              </footer>
            </blockquote>
          </div>
        </div>
      </section>

      {/* ===== PRICING SECTION ===== */}
      <section ref={pricingRef} id="precio" className="py-24 px-6 bg-gradient-to-b from-[#1a2e40] to-[#0f1f2e]" data-id="element-125">
        <div className="max-w-lg mx-auto text-center space-y-8" data-id="element-126">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-amber-100" data-id="element-127">
            Tu día con propósito
            <br data-id="element-128" />
            en solo 15 minutos diarios
          </h2>
          <p className="text-lg text-amber-200/70" data-id="element-129">
            Invierte en ti misma. Mereces sentirte bien cada día.
          </p>

          {/* Price Card */}
          <div className="bg-white/5 backdrop-blur border border-amber-500/30 rounded-2xl p-8 space-y-6 shadow-2xl" data-id="element-130">
            <div className="space-y-2" data-id="element-131">
              <p className="text-amber-400 uppercase tracking-widest text-sm font-bold" data-id="element-132">
                Edición Digital Completa
              </p>
              <div className="flex items-baseline justify-center gap-2" data-id="element-133">
                <span className="text-5xl md:text-6xl font-serif font-bold text-white" data-id="element-134">
                  $9.99
                </span>
                <span className="text-amber-200/60 text-lg" data-id="element-135">USD</span>
              </div>
              <p className="text-amber-200/50 text-sm line-through" data-id="element-136">$19.99</p>
              <p className="text-amber-400 text-sm font-bold" data-id="element-137">
                50% de descuento — Oferta limitada
              </p>
            </div>

            <ul className="text-left space-y-3 text-amber-100/90 text-lg" data-id="element-138">
              <li className="flex items-start gap-3" data-id="element-139">
                <CheckCircleIcon className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-140" />
                <span data-id="element-141">10 capítulos completos con ejercicios prácticos</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-142">
                <CheckCircleIcon className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-143" />
                <span data-id="element-144">
                  Método de micro-hábitos flexibles por nivel de energía
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-145">
                <CheckCircleIcon className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-146" />
                <span data-id="element-147">Checklists, plantillas y diarios descargables</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-148">
                <CheckCircleIcon className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-149" />
                <span data-id="element-150">Acceso inmediato tras la compra</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-151">
                <CheckCircleIcon className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-152" />
                <span data-id="element-153">Actualizaciones gratuitas de por vida</span>
              </li>
            </ul>

            <Button href="https://vitalias.it.com/cart/44816643162166:1" size="xl" className="w-full bg-amber-600 hover:bg-amber-500 text-white border-none shadow-xl transform transition hover:scale-105" data-id="element-154">
              <ShoppingCartIcon className="mr-3 h-7 w-7" data-id="element-155" />
              Comprar Ahora — $9.99
            </Button>
          </div>

          <p className="text-sm text-amber-200/30" data-id="element-156">
            Satisfacción garantizada · Pago seguro · Acceso inmediato
          </p>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-16 px-6 bg-[#0f1f2e] text-center" data-id="element-157">
        <div className="max-w-2xl mx-auto space-y-6" data-id="element-158">
          <p className="text-2xl md:text-3xl font-serif text-amber-100 leading-relaxed" data-id="element-159">
            "La vitalidad no termina a los 65.
            <br data-id="element-160" />
            <span className="text-amber-400" data-id="element-161">
              Redescúbrela con micro-acciones."
            </span>
          </p>
          <Button href="https://vitalias.it.com/cart/44816643162166:1" size="lg" className="bg-amber-600 hover:bg-amber-500 text-white border-none shadow-xl transform transition hover:scale-105" data-id="element-162">
            <ShoppingCartIcon className="mr-2 h-5 w-5" data-id="element-163" />
            Comprar Ahora
          </Button>
        </div>
      </section>

      <Footer data-id="element-164" />
    </div>;
}